import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getPageViewName,
  sendHitRequest,
} from "../helpers/hitTracking";

const HitTrackingContext = createContext({
  trackHit: () => Promise.resolve(),
  trackInteraction: () => Promise.resolve(),
});

const normalizeHitPart = (value) => String(value ?? "").trim();

export function HitTrackingProvider({ children }) {
  const location = useLocation();
  const { token, accountId } = useSelector((state) => state.auth);
  const pageName = getPageViewName(location.pathname);
  const lastTrackedNavigationRef = useRef(null);

  const trackHit = useCallback(
    (name, type = "pageView") =>
      sendHitRequest({ name, type, token, accountId }),
    [accountId, token]
  );

  const trackInteraction = useCallback(
    (interactionName, type = "pageView") => {
      const normalizedInteractionName = normalizeHitPart(interactionName);

      if (!pageName || !normalizedInteractionName) {
        return Promise.resolve();
      }

      return trackHit(`${pageName}/${normalizedInteractionName}`, type);
    },
    [pageName, trackHit]
  );

  useEffect(() => {
    if (!token || !accountId || !pageName) {
      return;
    }

    const navigationKey = [
      accountId,
      location.key,
      location.pathname,
      location.search,
      location.hash,
      pageName,
    ].join("|");

    if (lastTrackedNavigationRef.current === navigationKey) {
      return;
    }

    lastTrackedNavigationRef.current = navigationKey;
    void trackHit(pageName);
  }, [
    accountId,
    location.hash,
    location.key,
    location.pathname,
    location.search,
    pageName,
    token,
    trackHit,
  ]);

  const value = useMemo(
    () => ({ trackHit, trackInteraction }),
    [trackHit, trackInteraction]
  );

  return (
    <HitTrackingContext.Provider value={value}>
      {children}
    </HitTrackingContext.Provider>
  );
}

export const useHitTracking = () => useContext(HitTrackingContext);
